import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import KetLetterInput from './KetLetterInput';
import { ketNumberMap } from './ketNumbering';

const KetReadingPart3b = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach((qId) => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const { shared_options, dialogue, image_url, example_answer } = exerciseData.content_data;
  const letters = (shared_options || []).map(option => option.id);
  // Image mode: the whole conversation (incl. options A-H) is served as a scan
  // of the original page — used when the source text cannot be digitized.
  const imageMode = !!image_url && !(dialogue && dialogue.length > 0);
  const mediaPrefix = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';
  const hasOptionTexts = (shared_options || []).some(o => o.image_text || o.option_text || o.text);

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const numberMap = ketNumberMap(exerciseData);

  // Map each {{n}} placeholder in the dialogue to its question:
  // match by order_index first, otherwise take the next unassigned question in order.
  const tokenRegex = /\{\{(\d+)\}\}/g;
  const tokenNumbers = [];
  (dialogue || []).forEach(line => {
    const matches = String(line.line || '').match(tokenRegex) || [];
    matches.forEach(token => tokenNumbers.push(parseInt(token.replace(/\D/g, ''), 10)));
  });
  const questionByToken = {};
  const usedQuestionIds = new Set();
  tokenNumbers.forEach(n => {
    let question = sortedQuestions.find(q => q.order_index === n && !usedQuestionIds.has(q.id));
    // fallback: next unassigned SCORED question — the example (points=0) has no
    // token in the dialogue and must never swallow a numbered gap's picker
    if (!question) question = sortedQuestions.find(q => !usedQuestionIds.has(q.id) && q.points !== 0);
    if (question) {
      usedQuestionIds.add(question.id);
      questionByToken[n] = question;
    }
  });

  const handleSelect = (questionId, letter) => {
    setAnswers(prev => ({ ...prev, [questionId]: letter }));
  };

  const renderLine = (line) => {
    const parts = String(line.line || '').split(/(\{\{\d+\}\})/g);
    return parts.map((part, index) => {
      const match = part.match(/\{\{(\d+)\}\}/);
      if (match) {
        const n = parseInt(match[1], 10);
        const question = questionByToken[n];
        if (!question) return part;
        const isExample = question.points === 0;
        return (
          <span key={index} className="inline-flex items-center gap-2 flex-wrap align-middle">
            <strong className={`exercise-gap-number align-middle ${isExample ? '!bg-amber-500 !text-white !border-amber-500' : ''}`}>
              {isExample ? 'Ví dụ' : `(${n})`}
            </strong>
            <KetLetterInput
              question={question}
              letters={letters}
              answer={answers[question.id]}
              onSelect={handleSelect}
              showHint={showHint}
              isReviewMode={isReviewMode}
              isHydrated={isHydrated}
            />
          </span>
        );
      }
      return part ? <span key={index} className="align-middle">{part}</span> : null;
    });
  };

  return (
    <div className="w-full h-full p-1 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-3xl mx-auto w-full py-4 space-y-4">
        {/* IMAGE MODE — original page scan (dialogue + options A-H) + one picker per gap */}
        {imageMode ? (
          <>
            <div className="bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-2 md:p-4 shadow-sm">
              <img
                src={`${mediaPrefix}${image_url}`}
                alt="Conversation"
                className="w-full h-auto rounded-xl"
              />
            </div>
            <div className="bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-4 md:p-6 shadow-sm space-y-3">
              {sortedQuestions.map((q) => {
                const isExample = q.points === 0;
                const num = numberMap[q.id];
                return (
                  <div key={q.id} className="flex items-center gap-3 flex-wrap">
                    <strong className={`exercise-gap-number ${isExample ? '!bg-amber-500 !text-white !border-amber-500' : ''}`}>
                      {isExample ? 'Ví dụ' : `(${num})`}
                    </strong>
                    <KetLetterInput
                      question={q}
                      letters={letters.length ? letters : ['A','B','C','D','E','F','G','H']}
                      answer={answers[q.id]}
                      onSelect={handleSelect}
                      showHint={showHint}
                      isReviewMode={isReviewMode}
                      isHydrated={isHydrated}
                    />
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {/* Dialogue with numbered blanks */}
            <div className="bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-4 md:p-8 shadow-sm">
              <div className="space-y-3">
                {(dialogue || []).map((line, idx) => (
                  <div key={idx} className="flex gap-3 items-start">
                    <span className="font-black text-slate-500 w-20 shrink-0 text-right text-sm pt-1.5">{line.speaker}:</span>
                    <div className="font-bold text-sm md:text-base leading-relaxed text-slate-700 dark:text-slate-200 flex-1 min-w-0 pt-1">
                      {renderLine(line)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Lettered answer options box (A–H) — only when option texts exist */}
            {hasOptionTexts && (
              <div className="bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-4 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {shared_options.map(option => {
                    const isExampleOption = example_answer && option.id === example_answer;
                    return (
                      <div key={option.id} className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${isExampleOption ? 'bg-slate-100 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700 opacity-60' : 'bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700'}`}>
                        <span className="font-display font-black text-amber-600 bg-amber-50 dark:bg-amber-900/30 w-7 h-7 rounded-full flex items-center justify-center border border-gray-200 shrink-0 text-sm">{option.id}</span>
                        <span className={`font-bold text-sm text-slate-700 dark:text-slate-200 ${isExampleOption ? 'line-through' : ''}`}>{option.image_text || option.option_text || option.text}</span>
                        {isExampleOption && <span className="ml-auto shrink-0 text-[10px] font-black text-amber-600 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-full border border-amber-200">Ví dụ</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default KetReadingPart3b;
